import { t } from '../i18n/translations';
import { JoinMeetingResponse, Participant, RecordingStatusPayload } from '../types';
import { SignalingClient } from '../services/signaling';
import { WebRTCManager } from '../services/webrtc';
import { AudioAnalyzer } from '../services/audio';
import { MeetingRecorder } from '../services/recording';
import { VideoGrid } from './VideoGrid';
import { ControlBar } from './ControlBar';
import { ChatDrawer } from './ChatDrawer';
import { ParticipantDrawer } from './ParticipantDrawer';
import { PollsDrawer } from './PollsDrawer';
import { FormsDrawer } from './FormsDrawer';
import { SettingsModal } from './SettingsModal';
import { RecordingModal } from './RecordingModal';
import { WhiteboardModal } from './WhiteboardModal';
import { NotesDrawer } from './NotesDrawer';
import { CaptionsService } from '../services/captions';

export class MeetingRoom {
  private joinData: JoinMeetingResponse;
  private localStream: MediaStream | null = null;

  private signaling: SignalingClient = new SignalingClient();
  private webrtc: WebRTCManager = new WebRTCManager();
  private audioAnalyzer: AudioAnalyzer = new AudioAnalyzer();
  private remoteAnalyzers: Map<string, AudioAnalyzer> = new Map();
  private recorder: MeetingRecorder = new MeetingRecorder();

  private videoGrid: VideoGrid = new VideoGrid();
  private controlBar: ControlBar = new ControlBar();
  private chatDrawer: ChatDrawer = new ChatDrawer();
  private participantDrawer: ParticipantDrawer = new ParticipantDrawer();
  private pollsDrawer: PollsDrawer = new PollsDrawer();
  private formsDrawer: FormsDrawer;
  private settingsModal: SettingsModal = new SettingsModal();
  private recordingModal: RecordingModal = new RecordingModal();
  private whiteboardModal: WhiteboardModal;
  private notesDrawer: NotesDrawer;
  private captionsService: CaptionsService = new CaptionsService();
  private captionTimeout: any = null;
  private isSimulatedUnstable = false;

  private participantsMap: Map<string, Participant> = new Map();
  private waitingList: Participant[] = [];

  private isWaiting = false;
  private isLocked = false;
  private isRecording = false;
  private startedAt = 0;
  private timerInterval: number | null = null;
  private unreadChat = 0;
  private speakerTimeout: any = null;
  private localParticipantData: Participant;

  // Gate for peer-connection establishment: in the waiting-room flow signaling
  // connects before webrtc.init() has run, and creating RTCPeerConnections
  // before that point builds them with empty ICE servers and no local tracks.
  private mediaStarted = false;

  public onLeave?: () => void;

  constructor(joinData: JoinMeetingResponse, localStream: MediaStream | null, initialVideoMuted = false, initialAudioMuted = false) {
    this.joinData = joinData;
    this.localStream = localStream;
    this.isWaiting = joinData.waiting_room_required;
    this.startedAt = joinData.started_at || Math.floor(Date.now() / 1000);
    this.localParticipantData = {
      id: joinData.participant_id,
      display_name: joinData.display_name,
      avatar_url: joinData.avatar_url,
      role: joinData.role,
      stream: localStream || undefined,
      audio_muted: initialAudioMuted,
      video_muted: initialVideoMuted,
      screen_sharing: false,
    };
    this.whiteboardModal = new WhiteboardModal(joinData.room_id);
    this.formsDrawer = new FormsDrawer(joinData.room_id);
    this.notesDrawer = new NotesDrawer(joinData.room_id);
    (window as any).__verveMeeting = this;
    (window as any).__verveMeeting.signaling = this.signaling;
  }

  public get currentUserId(): string {
    return this.joinData.participant_id;
  }

  public get currentUserRole(): string {
    return this.joinData.role;
  }

  public get signalingClient(): SignalingClient {
    return this.signaling;
  }

  public get participantsList(): Participant[] {
    return [this.localParticipantData, ...Array.from(this.participantsMap.values())];
  }

  public get participants(): Participant[] {
    return [this.localParticipantData, ...Array.from(this.participantsMap.values())];
  }

  public get localParticipant(): Participant {
    return this.localParticipantData;
  }

  public async init(container: HTMLElement) {
    if (this.isWaiting) {
      this.renderWaitingRoom(container);
      this.connectSignaling(container);
      return;
    }

    this.renderRoom(container);
    await this.startMediaAndSignaling(container);
  }

  private renderWaitingRoom(container: HTMLElement) {
    container.innerHTML = `
      <div class="lobby-container">
        <div class="lobby-card" style="display: flex; flex-direction: column; align-items: center; text-align: center; max-width: 500px;">
          <div style="font-size: 3.5rem; margin-bottom: 1rem; animation: pulse 2s infinite;">⏳</div>
          <h2>${t('waitingRoomTitle')}</h2>
          <p style="color: var(--text-secondary); margin-top: 8px; line-height: 1.5;">
            ${t('waitingRoomDesc')}
          </p>
          <button id="btn-leave-waiting" class="btn-secondary" style="margin-top: 2rem;">
            Cancel & Exit
          </button>
        </div>
      </div>
    `;

    container.querySelector('#btn-leave-waiting')?.addEventListener('click', () => {
      this.cleanup();
      if (this.onLeave) this.onLeave();
    });
  }

  private renderRoom(container: HTMLElement) {
    container.innerHTML = `
      <div class="meeting-wrapper">
        <!-- Top Bar -->
        <header class="meeting-header">
          <div class="meeting-meta">
            <span style="font-weight: 800; font-size: 1.15rem; flex-shrink: 0;" class="text-gradient">Verve</span>
            <div id="badge-room-code" class="room-code-badge" title="${t('copyLink')}">
              <span class="room-code-text">${this.joinData.room_id}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </div>
            <button id="btn-meeting-info" class="btn-icon-subtle" title="Meeting Information & Copyright" style="background: rgba(255,255,255,0.08); border: 1px solid var(--border-subtle); color: var(--text-secondary); width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0;" aria-label="Meeting Information">
              <span style="font-weight: 800; font-size: 0.85rem; font-family: serif;">ℹ</span>
            </button>
            ${this.isLocked ? `<span class="badge" style="background: var(--color-danger-bg); color: var(--color-danger); flex-shrink: 0;">🔒 Locked</span>` : ''}
          </div>

          <div style="display: flex; align-items: center; gap: 12px; flex-shrink: 0;">
            <div id="active-speaker-banner" class="active-speaker-banner" style="display: none;">
              <span class="voice-wave active">
                <span class="wave-bar"></span>
                <span class="wave-bar"></span>
                <span class="wave-bar"></span>
                <span class="wave-bar"></span>
              </span>
              <span>Speaking: <strong id="active-speaker-name"></strong></span>
            </div>
            <div id="recording-badge-container"></div>
            <div id="meeting-timer-display" class="meeting-timer">00:00:00</div>
            <div id="badge-network-health" style="display: flex; align-items: center; gap: 6px; font-size: 0.8rem; color: var(--color-success); cursor: pointer; padding: 4px 8px; border-radius: 4px; background: rgba(255,255,255,0.05);" title="Connection health status (click to toggle simulated unstable connection)">
              <span id="network-health-dot" style="width: 8px; height: 8px; border-radius: 50%; background: var(--color-success);"></span>
              <span id="network-health-text">HD Secure</span>
            </div>
          </div>
        </header>

        <!-- Reconnection Alert Banner -->
        <div id="meeting-reconnect-banner" class="reconnect-banner" style="display: none; position: absolute; top: 65px; left: 50%; transform: translateX(-50%); background: #dc2626; color: white; padding: 8px 18px; border-radius: 9999px; z-index: 1000; font-weight: 700; font-size: 0.85rem; box-shadow: 0 4px 15px rgba(0,0,0,0.5); align-items: center; gap: 10px;">
          <span style="width: 14px; height: 14px; border: 2px solid white; border-top-color: transparent; border-radius: 50%; display: inline-block; animation: spin 1s linear infinite;"></span>
          <span id="reconnect-banner-text">Connection Lost. Reconnecting...</span>
          <button id="btn-reconnect-now" style="background: white; color: #dc2626; border: none; border-radius: 9999px; padding: 2px 8px; font-size: 0.75rem; font-weight: 700; cursor: pointer;">Retry Now</button>
        </div>

        <!-- Main Stage -->
        <main class="meeting-stage">
          <div id="video-grid-mount" style="flex: 1; height: 100%; min-width: 0; min-height: 0; position: relative;"></div>
          <div id="side-drawer-mount"></div>

          <!-- Live Closed Captions Subtitle Overlay -->
          <div id="live-captions-overlay" class="live-captions-overlay" style="display: none; position: absolute; bottom: 25px; left: 50%; transform: translateX(-50%); max-width: 85%; background: rgba(15, 23, 42, 0.9); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.2); padding: 8px 20px; border-radius: 9999px; color: white; font-size: 0.95rem; font-weight: 600; text-align: center; z-index: 40; pointer-events: none; box-shadow: 0 4px 20px rgba(0,0,0,0.6);">
            <span id="caption-speaker" style="color: var(--brand-primary); margin-right: 6px;"></span>
            <span id="caption-text"></span>
          </div>
        </main>

        <!-- Bottom Controls -->
        <footer id="control-bar-mount"></footer>

        <!-- Hidden persistent audio container for incoming peer audio streams -->
        <div id="remote-audio-container" style="position: absolute; width: 0; height: 0; overflow: hidden; opacity: 0; pointer-events: none;"></div>
      </div>
    `;

    this.bindHeaderEvents(container);

    // Mount subcomponents
    const gridMount = container.querySelector('#video-grid-mount') as HTMLElement;
    const ctrlMount = container.querySelector('#control-bar-mount') as HTMLElement;

    if (gridMount) this.videoGrid.render(gridMount);
    if (ctrlMount) this.controlBar.render(ctrlMount);

    this.startMeetingTimer(container);
  }

  private startMeetingTimer(container: HTMLElement) {
    if (this.timerInterval) clearInterval(this.timerInterval);

    const updateTimer = () => {
      const nowSec = Math.floor(Date.now() / 1000);
      const elapsed = Math.max(0, nowSec - this.startedAt);
      const hrs = Math.floor(elapsed / 3600).toString().padStart(2, '0');
      const mins = Math.floor((elapsed % 3600) / 60).toString().padStart(2, '0');
      const secs = (elapsed % 60).toString().padStart(2, '0');

      const el = container.querySelector('#meeting-timer-display');
      if (el) el.textContent = `${hrs}:${mins}:${secs}`;
    };

    updateTimer();
    this.timerInterval = window.setInterval(updateTimer, 1000);
  }

  private updateRecordingHeader(container: HTMLElement, isRec: boolean, recorderName?: string) {
    const badgeContainer = container.querySelector('#recording-badge-container');
    if (!badgeContainer) return;

    if (isRec) {
      badgeContainer.innerHTML = `
        <div class="badge badge-recording" title="Recording active by ${recorderName || 'User'}">
          <span class="recording-dot"></span>
          <span>REC ${recorderName ? `(${recorderName})` : ''}</span>
        </div>
      `;
    } else {
      badgeContainer.innerHTML = '';
    }
  }

  private updateActiveSpeakerUI(displayName: string | null) {
    const banner = document.getElementById('active-speaker-banner');
    const nameEl = document.getElementById('active-speaker-name');
    if (this.speakerTimeout) {
      clearTimeout(this.speakerTimeout);
      this.speakerTimeout = null;
    }
    if (banner && nameEl) {
      if (displayName) {
        nameEl.textContent = displayName;
        banner.style.display = 'inline-flex';
        this.speakerTimeout = setTimeout(() => {
          if (banner) banner.style.display = 'none';
        }, 2500);
      } else {
        banner.style.display = 'none';
      }
    }
  }

  private bindHeaderEvents(container: HTMLElement) {
    container.querySelector('#badge-room-code')?.addEventListener('click', () => {
      const fullUrl = `${window.location.origin}/room/${this.joinData.room_id}`;
      navigator.clipboard.writeText(fullUrl).then(() => {
        alert(t('linkCopied'));
      });
    });

    container.querySelector('#btn-meeting-info')?.addEventListener('click', () => {
      this.showMeetingInfoModal(container);
    });

    container.querySelector('#badge-network-health')?.addEventListener('click', () => {
      this.isSimulatedUnstable = !this.isSimulatedUnstable;
      this.simulateNetworkQuality(this.isSimulatedUnstable ? 'unstable' : 'good', this.isSimulatedUnstable ? 350 : 35, this.isSimulatedUnstable ? 8.0 : 0);
    });

    container.querySelector('#btn-reconnect-now')?.addEventListener('click', () => {
      this.signaling.reconnectNow();
    });
  }

  private showMeetingInfoModal(container: HTMLElement) {
    const modalHost = document.getElementById('modal-root') || container;
    const existing = document.getElementById('meeting-info-modal-wrapper');
    if (existing) existing.remove();

    const wrapper = document.createElement('div');
    wrapper.id = 'meeting-info-modal-wrapper';
    wrapper.innerHTML = `
      <div class="modal-backdrop" id="meeting-info-backdrop" style="position: fixed; inset: 0; background: rgba(0,0,0,0.75); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 10000; padding: 1rem;">
        <div class="modal-content" style="background: var(--bg-card, #111827); border: 1px solid var(--border-subtle, rgba(255,255,255,0.15)); border-radius: 16px; max-width: 480px; width: 100%; text-align: center; padding: 2rem 1.5rem; box-shadow: 0 20px 40px rgba(0,0,0,0.6); animation: modalIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);">
          <div style="font-size: 2.25rem; margin-bottom: 8px;">⚡</div>
          <h2 style="font-size: 1.45rem; font-weight: 800; margin: 0 0 4px;" class="text-gradient">Verve Video Conferencing</h2>
          <p style="color: var(--text-secondary); font-size: 0.85rem; margin: 0 0 1.25rem;">Enterprise-Grade Ultra-Responsive Media Platform • Powered by Solveran</p>
          
          <div style="background: rgba(255,255,255,0.04); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 12px 14px; margin-bottom: 1.25rem; text-align: left;">
            <div style="font-size: 0.72rem; font-weight: 700; color: var(--text-muted); letter-spacing: 0.05em; margin-bottom: 4px;">ACTIVE ROOM ID</div>
            <div style="font-family: var(--font-mono); font-weight: 800; color: #38bdf8; font-size: 1.05rem;">${this.joinData.room_id}</div>
            <div style="margin-top: 10px; font-size: 0.72rem; font-weight: 700; color: var(--text-muted); letter-spacing: 0.05em; margin-bottom: 4px;">INVITATION LINK</div>
            <div style="font-size: 0.82rem; word-break: break-all; color: var(--text-primary); font-family: var(--font-mono); background: rgba(0,0,0,0.25); padding: 6px 8px; border-radius: 6px;">${window.location.origin}/room/${this.joinData.room_id}</div>
          </div>

          <div style="border-top: 1px solid var(--border-subtle); padding-top: 1rem; font-size: 0.85rem; color: var(--text-secondary); display: flex; align-items: center; justify-content: center; gap: 8px;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" stroke-width="2.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span style="font-weight: 600;">${t('endToEndEncrypted')}</span>
          </div>

          <button id="btn-close-meeting-info" class="btn-primary" style="width: 100%; margin-top: 1.25rem; justify-content: center; padding: 10px 16px; font-weight: 700; border-radius: 8px;">
            Close
          </button>
        </div>
      </div>
    `;

    modalHost.appendChild(wrapper);

    const close = () => wrapper.remove();
    wrapper.querySelector('#btn-close-meeting-info')?.addEventListener('click', close);
    wrapper.querySelector('#meeting-info-backdrop')?.addEventListener('click', (e) => {
      if (e.target === wrapper.querySelector('#meeting-info-backdrop')) close();
    });
  }

  private syncRosterToComponents(container: HTMLElement) {
    const others = Array.from(this.participantsMap.values());
    const fullList = [this.localParticipantData, ...others];
    this.videoGrid.setParticipants(others);
    this.chatDrawer.setParticipants(fullList);
    this.participantDrawer.setParticipants(fullList, this.waitingList);

    const totalCount = fullList.length;
    this.controlBar.updateState({ participantCount: totalCount });

    const ctrlMount = container.querySelector('#control-bar-mount') as HTMLElement;
    if (ctrlMount) this.controlBar.render(ctrlMount);
  }

  private async startMediaAndSignaling(container: HTMLElement) {
    // Acquire fallback media if none was passed or existing tracks are inactive
    if (!this.localStream || this.localStream.getTracks().every(t => t.readyState === 'ended')) {
      try {
        this.localStream = await this.webrtc.getLocalMedia();
      } catch (e) {
        console.warn('Failed to obtain fresh media:', e);
      }
    } else {
      this.webrtc.setLocalStream(this.localStream);
    }

    if (this.localStream) {
      if (this.localParticipantData.video_muted) {
        this.localStream.getVideoTracks().forEach(t => (t.enabled = false));
      }
      if (this.localParticipantData.audio_muted) {
        this.localStream.getAudioTracks().forEach(t => (t.enabled = false));
      }
    }

    // Set local participant state
    const localP: Participant = {
      ...this.localParticipantData,
      stream: this.localStream || undefined,
    };

    this.videoGrid.setLocalParticipant(localP);
    this.controlBar.updateState({
      role: this.joinData.role,
      videoMuted: this.localParticipantData.video_muted,
      audioMuted: this.localParticipantData.audio_muted,
    });
    this.chatDrawer.setCurrentUserId(this.joinData.participant_id);
    this.chatDrawer.setMeetingInfo(this.joinData.room_id, `Verve Meeting (${this.joinData.room_id})`, this.joinData.display_name);
    this.participantDrawer.setCurrentUser(this.joinData.participant_id, this.joinData.role);
    this.pollsDrawer.setCurrentUser(this.joinData.participant_id, this.joinData.role);
    this.formsDrawer.setCurrentUser(this.joinData.participant_id, this.joinData.role, this.joinData.display_name);
    (window as any).__verveMeetingRoom = this;
    (window as any).__verveVideoGrid = this.videoGrid;
    (window as any).__verveWebRTC = this.webrtc;

    // Setup Local Audio Analyzer for speaking border glow
    if (this.localStream && this.localStream.getAudioTracks().length > 0) {
      this.audioAnalyzer.attachStream(this.localStream);
      this.audioAnalyzer.onSpeakingChange = (speaking) => {
        // Speaking is highlight-only (CSS classes via setActiveSpeaker) — it
        // must never re-lay-out the grid or move tiles.
        this.videoGrid.setActiveSpeaker(speaking ? localP.id : null);
        this.participantDrawer.setSpeakingStatus(localP.id, speaking);
        this.updateActiveSpeakerUI(speaking ? `${localP.display_name} (You)` : null);
        this.signaling.send('active_speaker', { speaking, level: speaking ? 1.0 : 0.0 });
      };
    }

    // 1. Initialize WebRTC PeerConnection & P2P Mesh first
    await this.webrtc.init(this.joinData.ice_servers, this.signaling, this.joinData.participant_id, this.joinData.display_name);

    if (this.localStream) {
      this.webrtc.publishTracks(this.localStream);
    }

    // Media is ready: peer connections created from here on carry the local
    // tracks and the server-provided ICE servers.
    this.mediaStarted = true;

    // 2. Connect Signaling WebSocket
    if (!this.signaling.isConnected()) {
      this.connectSignaling(container);
    }

    // 3. Negotiate with peers already on the roster (covers the waiting-room
    // flow where room_state arrived before media was ready and connectToPeer
    // was deferred)
    for (const peerId of this.participantsMap.keys()) {
      this.webrtc.connectToPeer(peerId);
    }

    // Wire up subcomponent interactions
    this.wireSubcomponents(container);
  }

  private connectSignaling(container: HTMLElement) {
    this.signaling.connect(this.joinData.token, this.joinData.room_id);

    this.signaling.on('room_state', (payload: any) => {
      if (payload.locked !== undefined) this.isLocked = payload.locked;
      if (payload.started_at) {
        this.startedAt = payload.started_at;
        this.startMeetingTimer(container);
      }
      if (payload.participants) {
        this.participantsMap.clear();
        for (const p of payload.participants) {
          if (p.id !== this.joinData.participant_id) {
            this.participantsMap.set(p.id, p);
            if (this.mediaStarted) {
              this.webrtc.connectToPeer(p.id);
            }
          }
        }
        this.waitingList = payload.waiting_participants || [];
        this.syncRosterToComponents(container);
        this.updateWaitingRoomNotification(container);
      }
      if (payload.polls) {
        this.pollsDrawer.setPolls(payload.polls);
      }
      if (payload.active_form) {
        this.formsDrawer.setActiveForm(payload.active_form);
      }
    });

    this.signaling.on('waiting_room_update', (payload: any) => {
      this.waitingList = payload.waiting_participants || [];
      this.syncRosterToComponents(container);
      this.updateWaitingRoomNotification(container);
    });

    this.signaling.on('participant_joined', (payload: any) => {
      if (payload.participant && payload.participant.id !== this.joinData.participant_id) {
        const existing = this.participantsMap.get(payload.participant.id);
        this.participantsMap.set(payload.participant.id, {
          ...payload.participant,
          stream: existing?.stream,
          screen_stream: existing?.screen_stream,
        });
        this.syncRosterToComponents(container);
        if (this.mediaStarted) {
          this.webrtc.connectToPeer(payload.participant.id);
        }
      }
    });

    this.signaling.on('participant_left', (payload: any) => {
      this.participantsMap.delete(payload.participant_id);
      this.webrtc.removePeer(payload.participant_id);
      this.videoGrid.removeParticipant(payload.participant_id);
      container.querySelector(`#verve-audio-${payload.participant_id}`)?.remove();
      const analyzer = this.remoteAnalyzers.get(payload.participant_id);
      if (analyzer) {
        analyzer.stop();
        this.remoteAnalyzers.delete(payload.participant_id);
      }
      this.syncRosterToComponents(container);
    });

    this.signaling.on('media_state_changed', (payload: any) => {
      const p = this.participantsMap.get(payload.participant_id);
      if (p) {
        p.audio_muted = payload.audio_muted;
        p.video_muted = payload.video_muted;
        p.screen_sharing = payload.screen_sharing;
        if (!payload.screen_sharing) {
          p.screen_stream = undefined;
        }
        if (payload.screen_stream_id) {
          (p as any).screen_stream_id = payload.screen_stream_id;
        }
      }
      this.videoGrid.addOrUpdateParticipant({
        id: payload.participant_id,
        audio_muted: payload.audio_muted,
        video_muted: payload.video_muted,
        screen_sharing: payload.screen_sharing,
        screen_stream: payload.screen_sharing ? p?.screen_stream : undefined,
      } as Participant);
      this.syncRosterToComponents(container);
    });

    this.signaling.on('chat_message', (payload: any) => {
      this.chatDrawer.addMessage(payload);
      if (!this.chatDrawer.getIsOpen()) {
        this.unreadChat++;
        this.controlBar.updateState({ unreadChatCount: this.unreadChat });
        const ctrlMount = container.querySelector('#control-bar-mount') as HTMLElement;
        if (ctrlMount) this.controlBar.render(ctrlMount);
      }
    });

    this.signaling.on('reaction', (payload: any) => {
      this.spawnFloatingReaction(payload.emoji);
    });

    this.signaling.on('hand_raise', (payload: any) => {
      const p = this.participantsMap.get(payload.participant_id);
      if (p) {
        p.hand_raised = payload.raised;
      }
      this.videoGrid.addOrUpdateParticipant({
        id: payload.participant_id,
        hand_raised: payload.raised,
      } as Participant);
      this.participantDrawer.setHandRaised(payload.participant_id, payload.raised);
      this.syncRosterToComponents(container);
      if (payload.participant_id === this.joinData.participant_id) {
        this.controlBar.updateState({ handRaised: payload.raised });
        const ctrlMount = container.querySelector('#control-bar-mount') as HTMLElement;
        if (ctrlMount) this.controlBar.render(ctrlMount);
      }
    });

    this.signaling.on('active_speaker', (payload: any) => {
      const isSpeaking = payload.speaking !== false;
      // Highlight-only: never re-lay-out the grid on speech changes.
      this.videoGrid.setActiveSpeaker(isSpeaking ? payload.participant_id : null);
      this.participantDrawer.setSpeakingStatus(payload.participant_id, isSpeaking);
      this.updateActiveSpeakerUI(isSpeaking ? (payload.display_name || 'Participant') : null);
    });

    this.signaling.on('recording_status', (payload: RecordingStatusPayload) => {
      this.updateRecordingHeader(container, payload.is_recording, payload.recorder_name);
      const recId = payload.recorder_id;
      if (recId) {
        const p = this.participantsMap.get(recId);
        if (p) {
          p.is_recording = payload.is_recording;
        }
        this.participantDrawer.setRecordingStatus(recId, payload.is_recording);
      }
    });

    this.signaling.on('waiting_room_admitted', (payload: any) => {
      const admittedId = payload?.id || payload?.participant_id;
      if (this.isWaiting && admittedId === this.joinData.participant_id) {
        this.isWaiting = false;
        this.renderRoom(container);
        this.startMediaAndSignaling(container);
      }
      this.waitingList = this.waitingList.filter(w => w.id !== admittedId);
      this.syncRosterToComponents(container);
      this.updateWaitingRoomNotification(container);
    });

    this.signaling.on('waiting_room_rejected', (payload?: any) => {
      if (this.isWaiting && (!payload || payload.id === this.joinData.participant_id)) {
        alert('You were not admitted to the meeting.');
        this.cleanup();
        if (this.onLeave) this.onLeave();
      }
      if (payload?.id) {
        this.waitingList = this.waitingList.filter(w => w.id !== payload.id);
        this.syncRosterToComponents(container);
        this.updateWaitingRoomNotification(container);
      }
    });

    this.signaling.on('host_action', async (payload: any) => {
      const ctrlMount = container.querySelector('#control-bar-mount') as HTMLElement;
      if (payload.action === 'force_mute') {
        this.webrtc.setAudioMuted(true);
        this.controlBar.updateState({ audioMuted: true });
        if (ctrlMount) this.controlBar.render(ctrlMount);
        this.signaling.send('media_state_changed', {
          audio_muted: true,
          video_muted: !this.localStream?.getVideoTracks().some(t => t.enabled),
          screen_sharing: this.webrtc.isScreenSharing(),
        });
        alert('The host has muted your microphone.');
      } else if (payload.action === 'force_stop_video') {
        this.webrtc.setVideoMuted(true);
        this.controlBar.updateState({ videoMuted: true });
        if (ctrlMount) this.controlBar.render(ctrlMount);
        this.signaling.send('media_state_changed', {
          audio_muted: !this.localStream?.getAudioTracks().some(t => t.enabled),
          video_muted: true,
          screen_sharing: this.webrtc.isScreenSharing(),
        });
        alert('The host has turned off your video camera.');
      } else if (payload.action === 'force_stop_screenshare') {
        if (this.webrtc.isScreenSharing()) {
          this.webrtc.stopScreenShare();
          this.controlBar.updateState({ screenSharing: false });
          if (ctrlMount) this.controlBar.render(ctrlMount);
          this.signaling.send('media_state_changed', {
            audio_muted: !this.localStream?.getAudioTracks().some(t => t.enabled),
            video_muted: !this.localStream?.getVideoTracks().some(t => t.enabled),
            screen_sharing: false,
          });
          alert('The host has stopped your screen share.');
        }
      } else if (payload.action === 'kicked') {
        this.signaling.blockReconnect();
        alert('You were removed from the meeting by the host.');
        await this.autoSaveRecording();
        await this.cleanup();
        if (this.onLeave) this.onLeave();
      }
    });

    this.signaling.on('meeting_locked', (payload: any) => {
      this.isLocked = payload.locked;
      this.renderRoom(container);
    });

    this.signaling.on('meeting_ended', async () => {
      await this.autoSaveRecording();
      alert('The host has ended this meeting. Your recording has been automatically saved and downloaded.');
      await this.cleanup();
      if (this.onLeave) this.onLeave();
    });

    this.signaling.on('poll_created', (poll: any) => {
      this.pollsDrawer.addPoll(poll);
    });

    this.signaling.on('poll_vote', (poll: any) => {
      this.pollsDrawer.updatePoll(poll);
    });

    this.signaling.on('whiteboard_draw', (action: any) => {
      this.whiteboardModal.handleRemoteDraw(action);
    });

    this.signaling.on('form_state', (payload: any) => {
      this.formsDrawer.setActiveForm(payload);
      if (payload && payload.is_active && this.joinData.role !== 'host') {
        const banner = document.getElementById('active-speaker-banner');
        if (banner) {
          banner.innerHTML = `<span style="font-weight: 700; color: #34d399;">📋 Roll Call Open: Click Forms icon to submit</span>`;
          banner.style.display = 'inline-flex';
          setTimeout(() => { banner.style.display = 'none'; }, 6000);
        }
      }
    });

    this.signaling.on('form_response', (payload: any) => {
      this.formsDrawer.addResponse(payload);
    });

    this.signaling.on('form_closed', () => {
      this.formsDrawer.setActiveForm(null);
    });

    // Network Status & Quality Events
    this.signaling.on('network_status', (payload: any) => {
      const isSelf = payload.participant_id === this.joinData.participant_id;
      if (isSelf) {
        this.localParticipantData.network_unstable = (payload.quality === 'unstable' || payload.quality === 'poor');
        this.localParticipantData.network_rtt = payload.rtt;
        this.videoGrid.setLocalParticipant(this.localParticipantData);
      } else {
        const p = this.participantsMap.get(payload.participant_id);
        if (p) {
          p.network_unstable = (payload.quality === 'unstable' || payload.quality === 'poor');
          p.network_rtt = payload.rtt;
        }
      }
      this.syncRosterToComponents(container);
    });

    // Live Closed Captions
    this.signaling.on('live_captions', (payload: any) => {
      this.displayCaption(payload.sender_name || 'Participant', payload.text);
    });

    // Shared Collaborative Notes
    this.signaling.on('notes_update', (payload: any) => {
      this.notesDrawer.setContent(payload.content);
    });

    // Participant Role Updates (Co-Host / Transfer Host)
    this.signaling.on('participant_updated', (payload: any) => {
      const isSelf = payload.participant_id === this.joinData.participant_id;
      if (isSelf) {
        this.joinData.role = payload.role;
        if (this.localParticipant) {
          this.localParticipant.role = payload.role;
          this.localParticipant.is_cohost = payload.is_cohost;
        }
        this.controlBar.updateState({ role: payload.role });
        const ctrlMount = container.querySelector('#control-bar-mount') as HTMLElement;
        if (ctrlMount) this.controlBar.render(ctrlMount);
      }

      const p = this.participantsMap.get(payload.participant_id);
      if (p) {
        p.role = payload.role;
        p.is_cohost = payload.is_cohost;
      }
      this.syncRosterToComponents(container);
    });

    // Reconnection Handlers
    this.signaling.onReconnecting = (attempt, _delay) => {
      this.showReconnectBanner(`Connection Lost. Reconnecting (Attempt ${attempt})...`, true);
    };

    this.signaling.onReconnected = () => {
      this.showReconnectBanner(`✅ Reconnected to meeting!`, false);
      setTimeout(() => this.hideReconnectBanner(), 2500);
      this.syncRosterToComponents(container);
    };

    // Remote Track received: handle both audio playback and video mounting
    this.webrtc.onRemoteStream = (peerId, stream, track, isScreen) => {
      const publisherId = peerId;

      if (track.kind === 'audio') {
        const audioStream = stream || new MediaStream([track]);
        let audioEl = container.querySelector<HTMLAudioElement>(`#verve-audio-${publisherId}`);
        if (!audioEl) {
          audioEl = document.createElement('audio');
          audioEl.id = `verve-audio-${publisherId}`;
          audioEl.autoplay = true;
          (audioEl as any).playsInline = true;
          audioEl.volume = 1.0;
          audioEl.muted = false;
          container.querySelector('#remote-audio-container')?.appendChild(audioEl);
        }
        if (audioEl.srcObject !== audioStream) {
          audioEl.srcObject = audioStream;
        }
        audioEl.volume = 1.0;
        audioEl.muted = false;

        track.onunmute = () => {
          if (audioEl && audioEl.paused) audioEl.play().catch(() => {});
        };

        audioEl.play().catch(e => {
          console.warn('Autoplay audio deferred, awaiting user gesture:', e);
          this.showAudioUnlockBanner(container);
        });

        // Attach client-side sound energy analyzer for accurate speaking sound wave detection
        let analyzer = this.remoteAnalyzers.get(publisherId);
        if (!analyzer) {
          analyzer = new AudioAnalyzer();
          this.remoteAnalyzers.set(publisherId, analyzer);
          analyzer.onSpeakingChange = (speaking) => {
            // Highlight-only: never re-lay-out the grid on speech changes.
            this.videoGrid.setActiveSpeaker(speaking ? publisherId : null);
            this.participantDrawer.setSpeakingStatus(publisherId, speaking);
            const peer = this.participantsMap.get(publisherId);
            this.updateActiveSpeakerUI(speaking ? (peer?.display_name || 'Participant') : null);
          };
        }
        analyzer.attachStream(audioStream);
      } else if (track.kind === 'video') {
        const peer = this.participantsMap.get(publisherId);
        const isScreenShare = isScreen ||
          (peer && (peer as any).screen_stream_id && stream.id === (peer as any).screen_stream_id) ||
          publisherId.endsWith('-screen') ||
          (peer?.screen_sharing && peer.stream !== undefined && stream.id !== peer.stream.id);

        if (isScreenShare || publisherId.endsWith('-screen')) {
          const baseId = publisherId.replace('-screen', '');
          if (peer) {
            peer.screen_stream = stream;
            peer.screen_sharing = true;
          }
          this.videoGrid.attachRemoteScreenStream(baseId, stream);
        } else {
          if (peer) {
            peer.stream = stream;
            peer.video_muted = false;
          }
          this.videoGrid.attachRemoteStream(publisherId, stream);
        }
      }
    };

    // Universal audio auto-unlocker on any user interaction with the window
    const unlockAllAudios = () => {
      this.hideAudioUnlockBanner(container);
      container.querySelectorAll<HTMLAudioElement>('#remote-audio-container audio').forEach(el => {
        if (el.paused) el.play().catch(() => {});
      });
    };
    container.addEventListener('click', unlockAllAudios);
    container.addEventListener('touchstart', unlockAllAudios);
    window.addEventListener('keydown', unlockAllAudios);
  }

  private showAudioUnlockBanner(container: HTMLElement) {
    let banner = container.querySelector('#audio-unlock-banner') as HTMLElement;
    if (!banner) {
      banner = document.createElement('div');
      banner.id = 'audio-unlock-banner';
      banner.style.cssText = 'position: absolute; top: 64px; left: 50%; transform: translateX(-50%); z-index: 60; background: rgba(239, 68, 68, 0.95); color: white; padding: 8px 18px; border-radius: 9999px; font-size: 0.82rem; font-weight: 600; cursor: pointer; box-shadow: 0 4px 16px rgba(0,0,0,0.5); display: flex; align-items: center; gap: 8px; backdrop-filter: blur(8px);';
      banner.innerHTML = `<span>🔇</span><span>${t('audioBlockedNotice')}</span>`;
      banner.addEventListener('click', () => {
        container.querySelectorAll<HTMLAudioElement>('#remote-audio-container audio').forEach(el => {
          el.play().catch(() => {});
        });
        banner.remove();
      });
      container.querySelector('.meeting-stage')?.appendChild(banner);
    }
  }

  private hideAudioUnlockBanner(container: HTMLElement) {
    container.querySelector('#audio-unlock-banner')?.remove();
  }

  private wireSubcomponents(container: HTMLElement) {
    const sideMount = container.querySelector('#side-drawer-mount') as HTMLElement;
    const modalRoot = document.getElementById('modal-root') || container;
    const ctrlMount = container.querySelector('#control-bar-mount') as HTMLElement;

    this.videoGrid.onLowerHand = (pId) => {
      if (this.joinData.role === 'host' || this.joinData.role === 'moderator' || !!this.localParticipantData?.is_cohost) {
        this.signaling.send('host_action', { action: 'lower_hand', target_id: pId });
        this.videoGrid.addOrUpdateParticipant({ id: pId, hand_raised: false } as Participant);
        this.participantDrawer.setHandRaised(pId, false);
      }
    };

    this.controlBar.onToggleAudio = async (muted) => {
      this.localStream = await this.webrtc.toggleAudio(muted);
      this.localParticipantData.audio_muted = muted;
      this.localParticipantData.stream = this.localStream || undefined;
      this.videoGrid.setLocalParticipant({ ...this.localParticipantData });
      this.signaling.send('media_state_changed', {
        audio_muted: muted,
        video_muted: (this.controlBar as any).isVideoMuted,
        screen_sharing: (this.controlBar as any).isScreenSharing,
      });
    };

    this.controlBar.onToggleVideo = async (muted) => {
      this.localStream = await this.webrtc.toggleVideo(muted);
      this.localParticipantData.video_muted = muted;
      this.localParticipantData.stream = this.localStream || undefined;
      this.videoGrid.setLocalParticipant({ ...this.localParticipantData });
      this.signaling.send('media_state_changed', {
        audio_muted: (this.controlBar as any).isAudioMuted,
        video_muted: muted,
        screen_sharing: (this.controlBar as any).isScreenSharing,
      });
    };

    this.controlBar.onToggleScreenShare = async () => {
      const isSharing = (this.controlBar as any).isScreenSharing;
      if (!isSharing) {
        try {
          // Tell SFU and room participants in advance so track router classifies it as screen share
          this.localParticipantData.screen_sharing = true;
          this.signaling.send('media_state_changed', {
            audio_muted: (this.controlBar as any).isAudioMuted,
            video_muted: (this.controlBar as any).isVideoMuted,
            screen_sharing: true,
          });

          const stream = await this.webrtc.startScreenShare();
          if (stream) {
            this.controlBar.updateState({ screenSharing: true });
            this.localParticipantData.screen_sharing = true;
            this.localParticipantData.screen_stream = stream;
            this.videoGrid.setLocalParticipant({ ...this.localParticipantData, screen_stream: stream });
            this.videoGrid.addOrUpdateParticipant({
              id: this.joinData.participant_id,
              screen_sharing: true,
              screen_stream: stream,
              stream: this.localStream || undefined,
            } as Participant);
            this.participantDrawer.setScreenShareStatus(this.joinData.participant_id, true);
            this.signaling.send('media_state_changed', {
              audio_muted: (this.controlBar as any).isAudioMuted,
              video_muted: (this.controlBar as any).isVideoMuted,
              screen_sharing: true,
              screen_stream_id: stream.id,
            });
          } else {
            // Cancelled by user or unsupported platform
            this.localParticipantData.screen_sharing = false;
            this.localParticipantData.screen_stream = undefined;
            this.videoGrid.setLocalParticipant({ ...this.localParticipantData, screen_stream: undefined });
            this.controlBar.updateState({ screenSharing: false });
            this.signaling.send('media_state_changed', {
              audio_muted: (this.controlBar as any).isAudioMuted,
              video_muted: (this.controlBar as any).isVideoMuted,
              screen_sharing: false,
            });
          }
        } catch (err: any) {
          console.warn('Screen share failed to start:', err);
          this.localParticipantData.screen_sharing = false;
          this.localParticipantData.screen_stream = undefined;
          this.videoGrid.setLocalParticipant({ ...this.localParticipantData, screen_stream: undefined });
          this.controlBar.updateState({ screenSharing: false });
          this.signaling.send('media_state_changed', {
            audio_muted: (this.controlBar as any).isAudioMuted,
            video_muted: (this.controlBar as any).isVideoMuted,
            screen_sharing: false,
          });
          const banner = document.getElementById('active-speaker-banner');
          if (banner) {
            banner.innerHTML = `<span style="color: #f87171; font-weight: 700;">⚠️ ${err.message || 'Screen sharing unavailable on this browser'}</span>`;
            banner.style.display = 'inline-flex';
            setTimeout(() => { banner.style.display = 'none'; }, 4500);
          }
        }
      } else {
        await this.webrtc.stopScreenShare();
        this.controlBar.updateState({ screenSharing: false });
        this.localParticipantData.screen_sharing = false;
        this.localParticipantData.screen_stream = undefined;
        this.videoGrid.setLocalParticipant({ ...this.localParticipantData, screen_stream: undefined });
        this.signaling.send('media_state_changed', {
          audio_muted: (this.controlBar as any).isAudioMuted,
          video_muted: (this.controlBar as any).isVideoMuted,
          screen_sharing: false,
        });
        this.videoGrid.addOrUpdateParticipant({
          id: this.joinData.participant_id,
          screen_sharing: false,
          screen_stream: undefined,
          stream: this.localStream || undefined,
        } as Participant);
        this.participantDrawer.setScreenShareStatus(this.joinData.participant_id, false);
      }
      if (ctrlMount) this.controlBar.render(ctrlMount);
    };

    // When screen share track ends natively via browser toolbar
    this.webrtc.onScreenShareEnded = () => {
      this.controlBar.updateState({ screenSharing: false });
      this.localParticipantData.screen_sharing = false;
      this.localParticipantData.screen_stream = undefined;
      this.videoGrid.setLocalParticipant({ ...this.localParticipantData, screen_stream: undefined });
      this.signaling.send('media_state_changed', {
        audio_muted: (this.controlBar as any).isAudioMuted,
        video_muted: (this.controlBar as any).isVideoMuted,
        screen_sharing: false,
      });
      this.videoGrid.addOrUpdateParticipant({
        id: this.joinData.participant_id,
        screen_sharing: false,
        screen_stream: undefined,
        stream: this.localStream || undefined,
      } as Participant);
      this.participantDrawer.setScreenShareStatus(this.joinData.participant_id, false);
      if (ctrlMount) this.controlBar.render(ctrlMount);
    };

    // Meeting Recording Toggle
    this.controlBar.onToggleRecord = async () => {
      if (!this.isRecording) {
        const started = await this.recorder.start(this.localStream);
        if (started) {
          this.isRecording = true;
          this.controlBar.updateState({ recording: true });
          if (ctrlMount) this.controlBar.render(ctrlMount);
          this.updateRecordingHeader(container, true, this.joinData.display_name);
          this.participantDrawer.setRecordingStatus(this.joinData.participant_id, true);
          this.signaling.send('recording_status', {
            is_recording: true,
            recorder_id: this.joinData.participant_id,
            recorder_name: this.joinData.display_name,
          });
        }
      } else {
        const result = await this.recorder.stop();
        this.isRecording = false;
        this.controlBar.updateState({ recording: false });
        if (ctrlMount) this.controlBar.render(ctrlMount);
        this.updateRecordingHeader(container, false);
        this.participantDrawer.setRecordingStatus(this.joinData.participant_id, false);
        this.signaling.send('recording_status', {
          is_recording: false,
          recorder_id: this.joinData.participant_id,
          recorder_name: this.joinData.display_name,
        });
        if (result) {
          this.recordingModal.render(modalRoot, result, this.joinData.room_id);
        }
      }
    };

    this.controlBar.onSendReaction = (emoji) => {
      this.signaling.send('reaction', { emoji });
      this.spawnFloatingReaction(emoji);
    };

    this.controlBar.onToggleHandRaise = (raised) => {
      this.signaling.send('hand_raise', { raised });
      this.videoGrid.addOrUpdateParticipant({
        id: this.joinData.participant_id,
        hand_raised: raised,
      } as Participant);
      this.participantDrawer.setHandRaised(this.joinData.participant_id, raised);
    };

    this.controlBar.onToggleParticipants = () => {
      this.chatDrawer.hide();
      this.pollsDrawer.hide();
      this.formsDrawer.hide();
      this.participantDrawer.toggle(sideMount);
    };

    this.controlBar.onToggleChat = () => {
      this.unreadChat = 0;
      this.controlBar.updateState({ unreadChatCount: 0 });
      this.participantDrawer.hide();
      this.pollsDrawer.hide();
      this.formsDrawer.hide();
      this.chatDrawer.toggle(sideMount);
    };

    this.controlBar.onTogglePolls = () => {
      this.chatDrawer.hide();
      this.participantDrawer.hide();
      this.formsDrawer.hide();
      this.pollsDrawer.toggle(sideMount);
    };

    this.controlBar.onToggleForms = () => {
      this.chatDrawer.hide();
      this.participantDrawer.hide();
      this.pollsDrawer.hide();
      this.formsDrawer.toggle(sideMount);
    };

    this.formsDrawer.onPublishForm = (form) => {
      this.signaling.send('form_create', form);
    };

    this.formsDrawer.onSubmitResponse = (formId, values) => {
      this.signaling.send('form_submit', { form_id: formId, values });
    };

    this.formsDrawer.onCloseForm = (formId) => {
      this.signaling.send('form_close', { form_id: formId });
    };

    this.controlBar.onToggleNotes = () => {
      this.chatDrawer.hide();
      this.participantDrawer.hide();
      this.pollsDrawer.hide();
      this.formsDrawer.hide();
      this.notesDrawer.toggle(sideMount);
    };

    this.notesDrawer.onNotesChange = (content) => {
      this.signaling.send('notes_update', { content });
    };

    this.controlBar.onToggleCaptions = (active) => {
      if (active) {
        this.captionsService.start();
        this.displayCaption(this.localParticipant?.display_name || 'You', 'Live Closed Captions active...');
      } else {
        this.captionsService.stop();
        const overlay = document.getElementById('live-captions-overlay');
        if (overlay) overlay.style.display = 'none';
      }
    };

    this.captionsService.onTranscript = (text, isFinal) => {
      this.displayCaption(this.localParticipant?.display_name || 'You', text);
      this.signaling.send('live_captions', { text, is_final: isFinal });
    };

    this.controlBar.onToggleWhiteboard = () => {
      this.whiteboardModal.toggle(modalRoot);
    };

    this.whiteboardModal.onDrawAction = (action) => {
      this.signaling.send('whiteboard_draw', action);
    };

    this.controlBar.onOpenSettings = () => {
      this.settingsModal.render(modalRoot);
    };

    this.controlBar.onLeave = async () => {
      await this.confirmAndSaveBeforeExit(modalRoot, async () => {
        await this.autoSaveRecording();
        await this.cleanup();
        if (this.onLeave) this.onLeave();
      });
    };

    this.controlBar.onEndMeeting = async () => {
      await this.confirmAndSaveBeforeExit(modalRoot, async () => {
        await this.autoSaveRecording();
        this.signaling.send('host_action', { action: 'end' });
        const hostToken = sessionStorage.getItem(`verve_host_token_${this.joinData.room_id}`) || '';
        await fetch(`/api/v1/meetings/${this.joinData.room_id}/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Host-Token': hostToken },
          body: JSON.stringify({ host_token: hostToken }),
        }).catch(() => {});
        await this.cleanup();
        if (this.onLeave) this.onLeave();
      });
    };

    // Chat events
    this.chatDrawer.onSendMessage = (content, targetId, attachment) => {
      this.signaling.send('chat_message', {
        content,
        target_id: targetId,
        attachment_url: attachment?.url,
        attachment_name: attachment?.name,
        attachment_type: attachment?.type,
        attachment_size: attachment?.size,
      });
    };

    // Participant moderation events
    this.participantDrawer.onHostAction = (action, targetId) => {
      if (action === 'lower_hand' && targetId) {
        this.videoGrid.addOrUpdateParticipant({ id: targetId, hand_raised: false } as Participant);
        this.participantDrawer.setHandRaised(targetId, false);
      }
      if (action === 'admit' || action === 'admit_all' || action === 'reject' || action === 'reject_all') {
        if (action === 'admit' && targetId) {
          this.waitingList = this.waitingList.filter(w => w.id !== targetId);
        } else if (action === 'reject' && targetId) {
          this.waitingList = this.waitingList.filter(w => w.id !== targetId);
        } else {
          this.waitingList = [];
        }
        this.updateWaitingRoomNotification(container);
      }
      this.signaling.send('host_action', { action, target_id: targetId });
    };

    // Polls events
    this.pollsDrawer.onCreatePoll = (question, options, isAnonymous) => {
      this.signaling.send('poll_created', { question, options, is_anonymous: isAnonymous });
    };

    this.pollsDrawer.onVote = (pollId, optionIdx) => {
      this.signaling.send('poll_vote', { poll_id: pollId, option_idx: optionIdx });
    };

    this.pollsDrawer.onClosePoll = (pollId) => {
      this.signaling.send('poll_closed', { poll_id: pollId });
    };

    // Settings changes
    this.settingsModal.onSettingsChanged = (settings) => {
      if (settings.audioOnly) {
        this.webrtc.setVideoMuted(true);
        this.controlBar.updateState({ videoMuted: true });
        if (ctrlMount) this.controlBar.render(ctrlMount);
      }
    };
  }

  private async confirmAndSaveBeforeExit(modalRoot: HTMLElement, onProceed: () => Promise<void>) {
    const hasWhiteboard = this.whiteboardModal.hasDrawings();
    const hasRecording = this.isRecording || Boolean(this.recorder.getLastResult()?.blob?.size);

    if (!hasWhiteboard && !hasRecording) {
      await onProceed();
      return;
    }

    const modal = document.createElement('div');
    modal.id = 'modal-unsaved-work';
    modal.className = 'modal-backdrop';
    modal.style.zIndex = '2000';
    modal.style.padding = '1rem';

    modal.innerHTML = `
      <div class="unsaved-work-modal">
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="width: 44px; height: 44px; border-radius: 50%; background: rgba(245, 158, 11, 0.15); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #f59e0b; flex-shrink: 0;">
            ⚠️
          </div>
          <div>
            <h3 style="margin: 0; font-size: 1.15rem; color: white;">Save Unsaved Work Before Leaving?</h3>
            <p style="margin: 2px 0 0 0; font-size: 0.8rem; color: var(--text-secondary);">
              You have active sketches and/or session recording that are not saved.
            </p>
          </div>
        </div>

        <div style="background: rgba(255, 255, 255, 0.04); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 12px 14px; display: flex; flex-direction: column; gap: 8px;">
          ${hasWhiteboard ? `
            <div style="display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: #38bdf8;">
              <span>🎨</span> <span><strong>Whiteboard:</strong> Active drawings will be downloaded as artifact.</span>
            </div>
          ` : ''}
          ${hasRecording ? `
            <div style="display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: #f87171;">
              <span>⏺️</span> <span><strong>Recording:</strong> Active meeting recording will be saved.</span>
            </div>
          ` : ''}
        </div>

        <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 4px;">
          <button id="btn-unsaved-save-all" class="btn-primary" style="padding: 10px; font-weight: 700; background: linear-gradient(135deg, #10b981, #059669);">
            💾 Save All & Leave
          </button>
          <button id="btn-unsaved-discard" class="btn-secondary" style="padding: 9px; color: #f87171; border-color: rgba(239, 68, 68, 0.3);">
            Leave Without Saving
          </button>
          <button id="btn-unsaved-cancel" class="btn-tool" style="padding: 8px; color: var(--text-muted); text-align: center; justify-content: center; width: 100%;">
            Cancel (Stay in Meeting)
          </button>
        </div>
      </div>
    `;

    modalRoot.appendChild(modal);

    modal.querySelector('#btn-unsaved-save-all')?.addEventListener('click', async () => {
      const btn = modal.querySelector('#btn-unsaved-save-all') as HTMLButtonElement;
      if (btn) {
        btn.disabled = true;
        btn.innerText = 'Saving...';
      }
      try {
        if (hasWhiteboard) {
          await this.whiteboardModal.exportAllPagesDefault();
        }
        if (hasRecording) {
          await this.autoSaveRecording();
        }
      } catch (e) {
        console.warn('Error during auto-save before exit:', e);
      }
      modal.remove();
      await onProceed();
    });

    modal.querySelector('#btn-unsaved-discard')?.addEventListener('click', async () => {
      if (this.isRecording) {
        this.isRecording = false;
        try { await this.recorder.stop(); } catch (_) {}
        this.recorder.clearLastResult();
      }
      modal.remove();
      await onProceed();
    });

    modal.querySelector('#btn-unsaved-cancel')?.addEventListener('click', () => {
      modal.remove();
    });
  }

  private async autoSaveRecording() {
    let result = this.recorder.getLastResult();
    if (this.isRecording) {
      this.isRecording = false;
      const stopped = await this.recorder.stop();
      if (stopped) result = stopped;
    }

    if (result && result.blob && result.blob.size > 0) {
      const filename = `verve-${this.joinData.room_id}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.webm`;
      const a = document.createElement('a');
      a.href = result.url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      this.recorder.clearLastResult();
      console.log('Recording auto-saved to downloads on meeting finish:', filename);
    }
  }

  private spawnFloatingReaction(emoji: string) {
    const reactionContainer = document.getElementById('reaction-container');
    if (!reactionContainer) return;

    const el = document.createElement('div');
    el.className = 'floating-reaction';
    el.innerText = emoji;
    el.style.left = `${Math.floor(Math.random() * 80) + 10}%`;

    reactionContainer.appendChild(el);
    setTimeout(() => {
      el.remove();
    }, 3000);
  }

  private displayCaption(speakerName: string, text: string) {
    const overlay = document.getElementById('live-captions-overlay');
    const speakerEl = document.getElementById('caption-speaker');
    const textEl = document.getElementById('caption-text');
    if (!overlay || !speakerEl || !textEl) return;

    speakerEl.textContent = `${speakerName}:`;
    textEl.textContent = text;
    overlay.style.display = 'block';

    if (this.captionTimeout) {
      clearTimeout(this.captionTimeout);
    }
    this.captionTimeout = setTimeout(() => {
      overlay.style.display = 'none';
      this.captionTimeout = null;
    }, 4500);
  }

  private showReconnectBanner(text: string, showSpinner: boolean) {
    const banner = document.getElementById('meeting-reconnect-banner');
    const textEl = document.getElementById('reconnect-banner-text');
    const spinner = banner?.querySelector('.reconnect-spinner') as HTMLElement;
    if (!banner || !textEl) return;

    textEl.textContent = text;
    if (spinner) {
      spinner.style.display = showSpinner ? 'inline-block' : 'none';
    }
    banner.style.display = 'flex';
  }

  private hideReconnectBanner() {
    const banner = document.getElementById('meeting-reconnect-banner');
    if (banner) banner.style.display = 'none';
  }

  public simulateNetworkQuality(quality: 'unstable' | 'good', rtt: number = 350, packetLoss: number = 8.0) {
    const dot = document.getElementById('network-health-dot');
    const text = document.getElementById('network-health-text');
    const isUnstable = quality === 'unstable';

    if (dot && text) {
      if (isUnstable) {
        dot.style.background = '#f59e0b';
        text.textContent = `📶 Unstable (${rtt}ms)`;
        text.style.color = '#f59e0b';
      } else {
        dot.style.background = '#10b981';
        text.textContent = 'HD Secure';
        text.style.color = '';
      }
    }

    this.localParticipantData.network_unstable = isUnstable;
    this.localParticipantData.network_rtt = rtt;
    this.videoGrid.setLocalParticipant(this.localParticipantData);

    this.signaling.send('network_status', {
      quality,
      rtt,
      packet_loss: packetLoss,
    });

    const mount = document.getElementById('video-grid-mount');
    if (mount) this.videoGrid.render(mount);
    const sideMount = document.getElementById('side-panel-mount');
    if (sideMount && this.participantDrawer.isOpen) {
      const fullList = [this.localParticipantData, ...Array.from(this.participantsMap.values())];
      this.participantDrawer.setParticipants(fullList, this.waitingList);
    }
  }

  private updateWaitingRoomNotification(container: HTMLElement) {
    const isHostOrMod = this.joinData.role === 'host' || this.joinData.role === 'moderator' || this.localParticipantData.is_cohost;
    let toast = container.querySelector('#waiting-room-toast') as HTMLElement;

    if (!isHostOrMod || this.waitingList.length === 0) {
      document.querySelectorAll('#waiting-room-toast').forEach(el => el.remove());
      return;
    }

    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'waiting-room-toast';
      toast.className = 'waiting-room-toast';
      container.appendChild(toast);
    }

    const count = this.waitingList.length;
    const names = this.waitingList.map(w => w.display_name).join(', ');

    toast.innerHTML = `
      <div class="waiting-toast-content">
        <div class="waiting-toast-icon">⏳</div>
        <div class="waiting-toast-info">
          <div class="waiting-toast-title">
            ${count === 1 ? `<strong>${this.waitingList[0].display_name}</strong> is in the waiting room` : `<strong>${count} people</strong> are in the waiting room`}
          </div>
          <div class="waiting-toast-subtitle">${names}</div>
        </div>
        <div class="waiting-toast-actions">
          <button id="btn-toast-admit-first" class="btn-toast-admit" title="Admit to meeting">
            ${count === 1 ? 'Admit' : 'Admit All'}
          </button>
          <button id="btn-toast-view-roster" class="btn-toast-view" title="Open participant roster">
            View
          </button>
          <button id="btn-toast-dismiss" class="btn-toast-close" title="Dismiss">&times;</button>
        </div>
      </div>
    `;

    toast.querySelector('#btn-toast-admit-first')?.addEventListener('click', () => {
      const targetId = this.waitingList[0]?.id || '';
      if (count === 1 && targetId) {
        this.signaling.send('host_action', { action: 'admit', target_id: targetId });
      } else {
        this.signaling.send('host_action', { action: 'admit_all', target_id: '' });
      }
      this.waitingList = [];
      document.querySelectorAll('#waiting-room-toast').forEach(el => el.remove());
    });

    toast.querySelector('#btn-toast-view-roster')?.addEventListener('click', () => {
      const drawerMount = container.querySelector('#side-panel-mount') as HTMLElement;
      if (drawerMount) {
        if (!this.participantDrawer.isOpen) {
          this.participantDrawer.toggle(drawerMount);
        }
      }
    });

    toast.querySelector('#btn-toast-dismiss')?.addEventListener('click', () => {
      toast?.remove();
    });
  }

  public async cleanup() {
    await this.autoSaveRecording();
    this.whiteboardModal.hide();
    if (this.speakerTimeout) {
      clearTimeout(this.speakerTimeout);
      this.speakerTimeout = null;
    }
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.audioAnalyzer.stop();
    this.remoteAnalyzers.forEach(a => a.stop());
    this.remoteAnalyzers.clear();
    this.webrtc.close();
    this.signaling.close();
    if (this.localStream) {
      this.localStream.getTracks().forEach(t => t.stop());
      this.localStream = null;
    }
  }
}
